import React from "react";
import { Grid, Typography, CardHeader, CardContent, Box } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts";
import { ChartCard } from "../Style/health-chart.style";
import AgeWiseGenderChart from "./AgeWiseGenderChart";

export default function DiseaseTabCharts({
    districtTitle,
    finalDonutDataWithPct,
    totalValue,
    finalDistChartData,
    allDistrictsChartData,
    tabValue,
    currentTabLabel,
    tabToConditionMap,
    ageWiseScreeningData,
    selectedDistrict
}) {
    // Normalize district name for 'ALL' case
    const normalizedDistrict = selectedDistrict === 'ALL' ? 'ALL DISTRICTS' : selectedDistrict;

    // Map UI tab labels to data keys (case-insensitive)
    const diseaseKeyMap = {
        'post stroke': 'STROKE',
        'mental health': 'Mental',
        'dm with htn': 'DMHTN',
        'bmi>=25': 'BMI',
        'cancer': 'CANCER',
        // Add other mappings if needed
    };

    // Get the data key, using the mapped value if it exists
    const dataKey = diseaseKeyMap[currentTabLabel?.toLowerCase()] || currentTabLabel;

    // Get the data for the current district and tab
    const districtData = ageWiseScreeningData?.[normalizedDistrict];
    const diseaseData = districtData?.[dataKey];

    return (
        <>
            {/* Age Wise Gender Chart - Full width at the top */}
            <Grid item xs={12} sx={{ mb: 0, width: '25%' }}>
                <Box sx={{ width: '100%', maxWidth: 1000, height: 552, margin: '0 auto' }}>
                    <AgeWiseGenderChart
                        ageWiseData={diseaseData}
                        selectedDistrict={selectedDistrict}
                        tabValue={tabValue}
                        diseaseName={currentTabLabel || 'Screening'}
                    />
                </Box>
            </Grid>

            {/* Existing charts below */}
            <Grid item xs={2} sx={{ width: "440px" }}>
                <ChartCard>
                    <CardHeader
                        title={
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", fontSize: "14px", textAlign: 'center', textDecorationLine: 'underline' }}
                            >
                                {`% Wise ${districtTitle}`}
                            </Typography>
                        }
                        sx={{ pb: 1 }}
                    />
                    <CardContent
                        sx={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: 480,
                            pt: 0,
                            overflowY: 'auto'
                        }}
                    >
                        <Box sx={{ position: "relative", width: 330, height: 420 }}>
                            <PieChart
                                series={[
                                    {
                                        data: finalDonutDataWithPct,
                                        innerRadius: 50,
                                        outerRadius: 100,
                                        paddingAngle: 2,
                                        cornerRadius: 2,
                                    },
                                ]}
                                height={200}
                                width={330}
                                slotProps={{
                                    legend: {
                                        direction: "column",
                                        position: { vertical: "bottom", horizontal: "middle" },
                                        itemGap: 2,
                                        labelStyle: { fontSize: 8 },
                                    },
                                }}
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "24%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    textAlign: "center",
                                    pointerEvents: "none",
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: "bold", color: "#333" }}
                                >
                                    {totalValue || 0}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </ChartCard>
            </Grid>
            <Grid item xs={12} md={6} sx={{ width: "600px" }}>
                <ChartCard>
                    <CardHeader
                        title={
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", fontSize: "14px", textAlign: 'center', textDecorationLine: 'underline' }}
                            >
                                {districtTitle}
                            </Typography>
                        }
                        sx={{ pb: 0 }}
                    />
                    <CardContent>
                        {finalDistChartData?.length > 0 &&
                            <BarChart
                                xAxis={[{ scaleType: "linear" }]}
                                yAxis={[
                                    {
                                        width: 100,
                                        scaleType: "band",
                                        data: finalDistChartData?.map((item) => item?.district),
                                        tickLabelStyle: { fontSize: 12 },
                                    },
                                ]}
                                series={(() => {
                                    // For tabs other than Home, show gender-wise data
                                    if (tabValue !== null && currentTabLabel && tabToConditionMap[currentTabLabel]) {
                                        const currentCondition = tabToConditionMap[currentTabLabel];
                                        const femaleKey = currentCondition.femaleKey;

                                        return [
                                            {
                                                data: finalDistChartData?.map((item) => {
                                                    // Find the district data to get female count
                                                    const districtData = allDistrictsChartData?.find(d => d.DISTRICT === item?.district);
                                                    const femaleCount = Number(districtData?.[femaleKey] || 0);
                                                    return femaleCount;
                                                }),
                                                label: "Female",
                                                color: "#ff69b4",
                                            },
                                            {
                                                data: finalDistChartData?.map((item) => {
                                                    // Find the district data to calculate male count
                                                    const districtData = allDistrictsChartData?.find(d => d.DISTRICT === item?.district);
                                                    const totalCount = Number(item?.district_count || 0);
                                                    const femaleCount = Number(districtData?.[femaleKey] || 0);
                                                    const maleCount = totalCount - femaleCount;
                                                    return Math.max(0, maleCount); // Ensure non-negative
                                                }),
                                                label: "Male",
                                                color: "#2196f3",
                                            },
                                        ];
                                    } else {
                                        // For Home tab, show total count only
                                        return [
                                            {
                                                data: finalDistChartData?.map((item) => item?.district_count),
                                                label: "Count",
                                                color: "#2196f3",
                                            },
                                        ];
                                    }
                                })()}
                                layout="horizontal"
                                height={430}
                                slotProps={{
                                    legend: {
                                        direction: "horizontal",
                                        position: { horizontal: "center", vertical: "bottom" },
                                    },
                                    tooltip: {
                                        // Enhance existing tooltip to include total count
                                        labelFormatter: (value, context) => {
                                            if (tabValue !== null && currentTabLabel && tabToConditionMap[currentTabLabel]) {
                                                const districtName = finalDistChartData?.[context.dataIndex]?.district;
                                                const totalCount = finalDistChartData?.[context.dataIndex]?.district_count || 0;
                                                return `${districtName} (Total: ${totalCount})`;
                                            }
                                            return value;
                                        }
                                    }
                                }}
                            />
                        }
                    </CardContent>
                </ChartCard>
            </Grid>
        </>
    );
}
